import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  LineChart, Line, BarChart, Bar as RBar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import api from "@/lib/api";
import { GlassCard, Kpi, Chip, Skeleton, Table, tip, PageHead } from "@/v2/admin/ui";
import { BarChart3, Filter, Users, Gauge, Trophy, Timer, Activity } from "lucide-react";

const RANGES = [["7", "أسبوع"], ["30", "شهر"], ["365", "سنة"], ["", "مخصص"]];

const dateBefore = (days) => new Date(Date.now() - days * 864e5).toISOString().slice(0, 10);
const todayStr = () => new Date().toISOString().slice(0, 10);

export default function Analytics() {
  const [range, setRange] = useState("30");
  const [from, setFrom] = useState(dateBefore(30));
  const [to, setTo] = useState(todayStr());
  const [dept, setDept] = useState("");
  const [chId, setChId] = useState("");
  const [result, setResult] = useState("");

  const { data: usersData } = useQuery({ queryKey: ["adm-users-lite"], queryFn: async () => (await api.get("/admin/users?limit=1")).data });
  const { data: challenges = [] } = useQuery({ queryKey: ["adm-challenges"], queryFn: async () => (await api.get("/admin/challenges")).data });
  const { data: a, isLoading } = useQuery({
    queryKey: ["adm-analytics", from, to, dept, chId, result],
    queryFn: async () => (await api.get(`/admin/analytics?date_from=${from}&date_to=${to}&department=${encodeURIComponent(dept)}&challenge_id=${chId}&result=${result}`)).data,
  });

  const setRangeQuick = (r) => {
    setRange(r);
    if (r) { setFrom(dateBefore(Number(r))); setTo(todayStr()); }
  };

  return (
    <div className="space-y-6" data-testid="admin-analytics">
      <PageHead icon={BarChart3} title="التحليلات" sub="تحليل الأداء حسب الفترة والإدارة والتحدي وحالة النتيجة." />

      <GlassCard className="p-4 sm:p-5 space-y-3">
        <div className="flex items-center gap-2 text-sm font-bold text-[#bcc8cf]"><Filter size={15} /> الفلاتر</div>
        <div className="flex flex-wrap gap-2">
          {RANGES.map(([k, l]) => (
            <button key={l} onClick={() => setRangeQuick(k)} data-testid={`an-range-${l}`}
              className={`px-4 h-9 rounded-full text-xs font-bold ${range === k ? "bg-gradient-to-l from-[#e3c799] to-[#bb9661] text-[#25343f]" : "bg-white/5 border border-white/10 text-[#bcc8cf]"}`}>{l}</button>
          ))}
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div><label className="v2-label">من</label><input type="date" dir="ltr" value={from} onChange={(e) => { setFrom(e.target.value); setRange(""); }} data-testid="an-from" className="v2-input" /></div>
          <div><label className="v2-label">إلى</label><input type="date" dir="ltr" value={to} onChange={(e) => { setTo(e.target.value); setRange(""); }} data-testid="an-to" className="v2-input" /></div>
          <div><label className="v2-label">الإدارة</label>
            <select value={dept} onChange={(e) => setDept(e.target.value)} data-testid="an-dept" className="v2-input">
              <option value="">كل الإدارات</option>
              {(usersData?.departments || []).map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div><label className="v2-label">التحدي</label>
            <select value={chId} onChange={(e) => setChId(e.target.value)} data-testid="an-ch" className="v2-input">
              <option value="">كل التحديات</option>
              {challenges.map((c) => <option key={c.id} value={c.id}>{`اليوم ${c.day} — ${c.title.slice(0, 30)}`}</option>)}
            </select>
          </div>
          <div><label className="v2-label">حالة النتيجة</label>
            <select value={result} onChange={(e) => setResult(e.target.value)} data-testid="an-result" className="v2-input">
              <option value="">الكل</option><option value="passed">ناجح</option><option value="failed">لم يجتز</option>
            </select>
          </div>
        </div>
      </GlassCard>

      {isLoading || !a ? <Skeleton className="h-80 w-full" /> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 sm:gap-4">
            <Kpi icon={Users} label="الموظفون" value={a.employees} />
            <Kpi icon={Activity} label="نسبة المشاركة" value={a.participation_rate} suffix="%" tone="gold" />
            <Kpi icon={Trophy} label="نسبة النجاح" value={a.pass_rate} suffix="%" />
            <Kpi icon={Gauge} label="متوسط الدرجات" value={a.avg_score} suffix="%" />
            <Kpi icon={Activity} label="عدد المحاولات" value={a.attempts_count} tone="gold" />
            <Kpi icon={Timer} label="متوسط زمن الاختبار (ث)" value={a.avg_duration} tone="muted" />
          </div>

          <GlassCard className="p-5">
            <h3 className="font-bold mb-4">الحضور والمحاولات خلال الفترة</h3>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={a.series}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
                <XAxis dataKey="day" tick={{ fill: "#bcc8cf", fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fill: "#bcc8cf", fontSize: 11 }} />
                <Tooltip {...tip} />
                <Line name="حضور" dataKey="checkins" stroke="#91cab8" strokeWidth={2} dot={false} />
                <Line name="محاولات" dataKey="attempts" stroke="#bb9661" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </GlassCard>

          <div className="grid lg:grid-cols-2 gap-6">
            <GlassCard className="p-5">
              <h3 className="font-bold mb-4">مسار الإنجاز (عدد من أكمل كل تحدٍ)</h3>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={a.funnel}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
                  <XAxis dataKey="day" tick={{ fill: "#bcc8cf", fontSize: 10 }} />
                  <YAxis allowDecimals={false} tick={{ fill: "#bcc8cf", fontSize: 11 }} />
                  <Tooltip {...tip} />
                  <RBar name="أكملوه" dataKey="completed" fill="#91cab8" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </GlassCard>
            <div className="space-y-6">
              <GlassCard className="p-5">
                <h3 className="font-bold mb-3">الأصعب / الأفضل</h3>
                <div className="space-y-2 text-sm">
                  {a.hardest.map((h) => (
                    <div key={`h${h.id}`} className="flex items-center gap-2"><Chip tone="red">أصعب</Chip><span className="flex-1 truncate">{h.title}</span><span className="text-[#bcc8cf]">{h.pass_rate}%</span></div>
                  ))}
                  {a.best.map((h) => (
                    <div key={`b${h.id}`} className="flex items-center gap-2"><Chip tone="emerald">الأفضل</Chip><span className="flex-1 truncate">{h.title}</span><span className="text-[#bcc8cf]">{h.pass_rate}%</span></div>
                  ))}
                  {a.hardest.length === 0 && <p className="text-[#bcc8cf]">لا توجد بيانات في هذه الفترة.</p>}
                </div>
              </GlassCard>
              <GlassCard className="p-5">
                <h3 className="font-bold mb-3">غير النشطين في الفترة ({a.inactive_employees.length})</h3>
                <div className="space-y-1.5 text-sm max-h-44 overflow-y-auto">
                  {a.inactive_employees.map((e) => (
                    <div key={e.id} className="flex justify-between"><span>{e.name}</span><span className="text-[#bcc8cf] text-xs">{e.last_checkin || "لا حضور"}</span></div>
                  ))}
                  {a.inactive_employees.length === 0 && <p className="text-[#bcc8cf]">جميع الموظفين نشطون.</p>}
                </div>
              </GlassCard>
            </div>
          </div>

          <Table head={["اليوم", "التحدي", "المحاولات", "نسبة النجاح", "متوسط الدرجة", "متوسط الزمن (ث)"]} testId="an-ch-table">
            {a.challenges.map((c) => (
              <tr key={c.id}>
                <td className="font-bold v2-gold-text">{c.day}</td>
                <td className="font-semibold">{c.title}</td>
                <td>{c.attempts}</td>
                <td><Chip tone={c.pass_rate >= 60 ? "emerald" : "red"}>{c.pass_rate}%</Chip></td>
                <td>{c.avg_score}%</td>
                <td className="text-[#bcc8cf]">{c.avg_duration}</td>
              </tr>
            ))}
          </Table>
        </>
      )}
    </div>
  );
}
